import React, { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import * as XLSX from "xlsx-js-style";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../api/axiosInstance";

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import StyledInput from "./StyledInput";
import { ModernButton } from "./ModernButton";
import AnimatedExportButton from "./AnimatedExportButton";

const IconDateButton = forwardRef(({ value, onClick, label }, ref) => {
  return (
    <button
      ref={ref}
      onClick={onClick}
      type="button"
      className="flex items-center gap-2 px-2 py-1 rounded-md bg-white"
    >
      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <span className="text-sm text-gray-800">{value || label}</span>
    </button>
  );
});

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function CompostShedTab({ fromDate, toDate, applyClicked, setFromDate, setToDate, setApplyClicked, activeRange, handleDateFilter }) {
  const [search, setSearch] = useState("");
  const [blockFilter, setBlockFilter] = useState([]);
  const [panchayatFilter, setPanchayatFilter] = useState([]);
  const [wasteTypeFilter, setWasteTypeFilter] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [blocks, setBlocks] = useState([]);
  const [panchayats, setPanchayats] = useState([]);
  const [wasteData, setWasteData] = useState([]);
  const [error, setError] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [imageModal, setImageModal] = useState({ show: false, images: [], currentIndex: 0, entryId: null });
  const roleId = parseInt(localStorage.getItem("roleId"));
  const savedBlockName = localStorage.getItem("blockName");
  const savedPanchayatName = localStorage.getItem("panchayatName");
  const recordsPerPage = 10;

  // Ensure incoming dates are not in the future
  useEffect(() => {
    const today = new Date();
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    try {
      if (fromDate) {
        const fd = new Date(fromDate);
        if (fd > today) setFromDate(fmt(today));
      }
      if (toDate) {
        const td = new Date(toDate);
        if (td > today) setToDate(fmt(today));
      }
    } catch (e) {}
  }, [fromDate, toDate, setFromDate, setToDate]);

  // ✅ Fetch WasteEntry Data
  useEffect(() => {
    const fetchWasteData = async () => {
      try {
        const response = await api.get("https://perambalur-swm-api.circularctech.com/api/WasteEntry");
        if (response.data.success && Array.isArray(response.data.data)) {
          setWasteData(response.data.data);
        } else {
          setError("Invalid response format from WasteEntry API");
        }
      } catch (err) {
        console.error("Error fetching waste entries:", err);
        setError("Failed to fetch WasteEntry data. Check backend connection.");
      }
    };
    fetchWasteData();
  }, []);

  // ✅ Fetch Blocks for Filter Dropdown
 useEffect(() => {
  const fetchBlocks = async () => {
    try {
      const res = await api.get("/Block");
      if (res.data.success && Array.isArray(res.data.data)) {
        const allBlocks = res.data.data;

        if (roleId === 1 || roleId === 2) {
          setBlocks(allBlocks);
        } else if (roleId === 3) {
          const filtered = allBlocks.filter(
            (b) => b.name?.toLowerCase() === savedBlockName?.toLowerCase()
          );
          setBlocks(filtered);
          setBlockFilter([savedBlockName]); // pre-select user’s block
        } else if (roleId === 4) {
          setBlocks(allBlocks); // dropdown hidden later
        }
      }
    } catch (err) {
      console.error("Error fetching blocks:", err);
    }
  };
  fetchBlocks();
}, [roleId, savedBlockName]);


  // ✅ Set Panchayats when Block Changes
  // useEffect(() => {
  //   if (blockFilter) {
  //     const selectedBlock = blocks.find((b) => b.name === blockFilter);
  //     setPanchayats(selectedBlock?.panchayats || []);
  //     setPanchayatFilter("");
  //   } else {
  //     setPanchayats([]);
  //     setPanchayatFilter("");
  //   }
  // }, [blockFilter, blocks]);

  useEffect(() => {
  if (roleId === 1 || roleId === 2) {
    if (blockFilter.length > 0) {
      const selected = blocks.filter((b) => blockFilter.includes(b.name));
      setPanchayats(selected.flatMap((b) => b.panchayats || []));
    } else {
      setPanchayats([]);
      setPanchayatFilter([]);
    }
  } else if (roleId === 3) {
    const selected = blocks.find(
      (b) => b.name?.toLowerCase() === savedBlockName?.toLowerCase()
    );
    setPanchayats(selected?.panchayats || []);
  } else if (roleId === 4) {
    setPanchayats([{ name: savedPanchayatName, panchayatId: 0 }]);
    setPanchayatFilter([savedPanchayatName]);
  }
}, [blocks, blockFilter, roleId, savedBlockName, savedPanchayatName]);



  // ✅ Enhanced Search & Filter Logic (including date range)
  const filteredData = wasteData.filter((d) => {
    const query = search.trim().toLowerCase();
    const matchesSearch =
      query === "" ||
      d.blockName?.toLowerCase().includes(query) ||
      d.panchayatName?.toLowerCase().includes(query) ||
      d.vehicleNumber?.toLowerCase().includes(query) ||
      d.vehicleType?.toLowerCase().includes(query);

    const matchesBlock =
      blockFilter.length > 0 ? blockFilter.includes(d.blockName) : true;
    const matchesPanchayat =
      panchayatFilter.length > 0 ? panchayatFilter.includes(d.panchayatName?.trim()) : true;

    // ✅ Date range filtering
    const matchesDateRange = (() => {
      if (!fromDate || !toDate) return true;
      const entryDate = new Date(d.date);
      const from = new Date(fromDate);
      const to = new Date(toDate);
      // Set time to start/end of day for accurate comparison
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      return entryDate >= from && entryDate <= to;
    })();

    // ✅ Waste type filtering
    const matchesWasteType = (() => {
      if (!wasteTypeFilter || wasteTypeFilter.length === 0) return true;
      return wasteTypeFilter.some((key) => (d[key] || 0) > 0);
    })();

    return matchesSearch && matchesBlock && matchesPanchayat && matchesDateRange && matchesWasteType;
  });

  // ✅ Pagination Logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  const getFormattedDate = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = now.toLocaleString("en-GB", { month: "short" });
  const year = now.getFullYear();
  return `${day}-${month}-${year}`;
};

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

const exportExcel = () => {
  const currentDate = getFormattedDate();
  const fileName = `Perambalur Compost Shed Weighment Details - ${currentDate}.xlsx`;

  // ✅ Prepare worksheet data dynamically based on wasteTypeFilter
  const worksheetData = filteredData.map((d, idx) => {
    const row = {
      "S.No": idx + 1,
      Date: formatDate(d.date),
      Block: d.blockName,
      Panchayat: d.panchayatName,
      "Vehicle #": d.vehicleNumber,
      Type: d.vehicleType,
    };

    const isAll = wasteTypeFilter.length === 0;

    if (isAll || wasteTypeFilter.includes("biodegradableKg")) {
      row["Bio Degradable (Kg)"] = d.biodegradableKg;
    }
    if (isAll || wasteTypeFilter.includes("nonDegradableKg")) {
      row["Non Degradable (Kg)"] = d.nonDegradableKg;
    }
    if (isAll || wasteTypeFilter.includes("recyclableDryKg")) {
      row["Recyclable (Kg)"] = d.recyclableDryKg;
    }
    if (isAll || wasteTypeFilter.includes("householdHazardousKg")) {
      row["Household Hazardous (Kg)"] = d.householdHazardousKg;
    }
    if (isAll || wasteTypeFilter.includes("mixedKg")) {
      row["Mixed (Kg)"] = d.mixedKg;
    }

    row["Total"] = d.totalKg;
    row["Images"] = "Available";
    return row;
  });

  const numCols = 6 + (wasteTypeFilter.length === 0 ? 5 : wasteTypeFilter.length) + 2;

  const worksheet = XLSX.utils.json_to_sheet(worksheetData, { origin: "A4" });
  const workbook = XLSX.utils.book_new();

  // ✅ Header rows
  XLSX.utils.sheet_add_aoa(
    worksheet,
    [
      ["Compost Shed Management"],
      ["Perambalur District Rural Development Administration (DRDA)"],
      [],
    ],
    { origin: "A1" }
  );

  // ✅ Merge header rows (A to end column)
  worksheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
  ];

  // ✅ Auto width
  const headerKeys = Object.keys(worksheetData[0]);
  worksheet["!cols"] = headerKeys.map((key) => ({
    wch: Math.max(
      key.length,
      ...worksheetData.map((r) => (r[key] ? r[key].toString().length : 0))
    ) + 3,
  }));

  // ✅ Center-align all cells
  Object.keys(worksheet).forEach((cell) => {
    if (cell[0] === "!") return;
    const cellRef = worksheet[cell];
    if (!cellRef.s) cellRef.s = {};
    cellRef.s.alignment = { horizontal: "center", vertical: "center", wrapText: true };
    const rowNum = parseInt(cell.match(/\d+/)?.[0]);
    if (rowNum === 4) cellRef.s.font = { bold: true };
  });

  ["A1", "A2"].forEach((ref) => {
    worksheet[ref].s = {
      alignment: { horizontal: "center", vertical: "center" },
      font: { bold: true, sz: ref === "A1" ? 16 : 14 },
    };
  });

  // ✅ Add total summation row
  const totalSum = filteredData.reduce((sum, d) => sum + (parseFloat(d.totalKg) || 0), 0);
  const totalRowIndex = worksheetData.length + 5;

  const emptyColsCount = numCols - 3;
  const totalRowArray = [...Array(emptyColsCount).fill(""), "Grand Total (Kg)", totalSum];

  XLSX.utils.sheet_add_aoa(
    worksheet,
    [totalRowArray],
    { origin: `A${totalRowIndex}` }
  );

  // ✅ Make Total row bold
  const getColLetter = (colIdx) => String.fromCharCode(65 + colIdx);
  const totalValColLetter = getColLetter(numCols - 2);
  const totalLabelColLetter = getColLetter(numCols - 3);

  const totalValCell = worksheet[`${totalValColLetter}${totalRowIndex}`];
  if (totalValCell) {
    totalValCell.s = {
      font: { bold: true, color: { rgb: "000000" } },
      alignment: { horizontal: "center" },
    };
  }
  const totalLabelCell = worksheet[`${totalLabelColLetter}${totalRowIndex}`];
  if (totalLabelCell) {
    totalLabelCell.s = {
      font: { bold: true, color: { rgb: "000000" } },
      alignment: { horizontal: "center" },
    };
  }

  // ✅ Add footer text (3 rows below total)
  const footerRow = totalRowIndex + 3;
  XLSX.utils.sheet_add_aoa(
    worksheet,
    [["Powered by CircularCarbon Technologies"]],
    { origin: `A${footerRow}` }
  );
  worksheet["!merges"].push({
    s: { r: footerRow - 1, c: 0 },
    e: { r: footerRow - 1, c: numCols - 1 },
  });

  worksheet[`A${footerRow}`].s = {
    alignment: { horizontal: "center", vertical: "center" },
    font: { italic: true, color: { rgb: "666666" }, sz: 12 },
  };

  XLSX.utils.book_append_sheet(workbook, worksheet, "WasteEntry Data");
  XLSX.writeFile(workbook, fileName);
};



  // ✅ PDF Export
  const exportPDF = () => {
  const currentDate = getFormattedDate();
  const fileName = `Perambalur Compost Shed Weighment Details - ${currentDate}.pdf`;

  const doc = new jsPDF("l", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // 🖼️ Add logo
  const logo = new Image();
  logo.src = "/cclogo.png";

  logo.onload = () => {
    doc.addImage(logo, "PNG", 14, 8, 18, 18);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Compost Shed Management", pageWidth / 2, 18, { align: "center" });
    doc.setFontSize(12);
    doc.text(
      "Perambalur District Rural Development Administration (DRDA)",
      pageWidth / 2,
      26,
      { align: "center" }
    );

    const tableColumn = [
      "S.No",
      "Date",
      "Block",
      "Panchayat",
      "Vehicle #",
      "Type",
    ];

    const isAll = wasteTypeFilter.length === 0;

    if (isAll || wasteTypeFilter.includes("biodegradableKg")) {
      tableColumn.push("Bio (Kg)");
    }
    if (isAll || wasteTypeFilter.includes("nonDegradableKg")) {
      tableColumn.push("Non-Bio (Kg)");
    }
    if (isAll || wasteTypeFilter.includes("recyclableDryKg")) {
      tableColumn.push("Recyclable (Kg)");
    }
    if (isAll || wasteTypeFilter.includes("householdHazardousKg")) {
      tableColumn.push("Hazardous (Kg)");
    }
    if (isAll || wasteTypeFilter.includes("mixedKg")) {
      tableColumn.push("Mixed (Kg)");
    }

    tableColumn.push("Total (Kg)", "Images");

    const tableRows = filteredData.map((d, idx) => {
      const row = [
        idx + 1,
        formatDate(d.date),
        d.blockName,
        d.panchayatName,
        d.vehicleNumber,
        d.vehicleType,
      ];

      if (isAll || wasteTypeFilter.includes("biodegradableKg")) {
        row.push(d.biodegradableKg);
      }
      if (isAll || wasteTypeFilter.includes("nonDegradableKg")) {
        row.push(d.nonDegradableKg);
      }
      if (isAll || wasteTypeFilter.includes("recyclableDryKg")) {
        row.push(d.recyclableDryKg);
      }
      if (isAll || wasteTypeFilter.includes("householdHazardousKg")) {
        row.push(d.householdHazardousKg);
      }
      if (isAll || wasteTypeFilter.includes("mixedKg")) {
        row.push(d.mixedKg);
      }

      row.push(d.totalKg, "Available");
      return row;
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      theme: "grid",
      styles: { halign: "center", valign: "middle", fontSize: 9 },
      headStyles: { fillColor: [230, 180, 50], textColor: 20, halign: "center" },
      didDrawPage: function () {
        // 🔷 Draw blue border
        doc.setDrawColor(0, 0, 255);
        doc.setLineWidth(1);
        doc.rect(5, 5, pageWidth - 10, pageHeight - 10);

        // Footer text
        doc.setFontSize(11);
        doc.text(
          "Powered by CircularCarbon Technologies",
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      },
    });

    doc.save(fileName);
  };
};


const getVisiblePages = () => {
  const maxVisible = 6;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = start + maxVisible - 1;

  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - maxVisible + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};


  // ✅ Close Export Menu when Clicking Outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".export-menu")) setShowExportMenu(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);


  // ✅ Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate, applyClicked, search, blockFilter, panchayatFilter, wasteTypeFilter]);

  // ✅ Handle image click and fetch images
  const handleImageClick = async (entryId) => {
    console.log('🖼️ Fetching images for entryId:', entryId);
    try {
      const response = await api.get(`/Image/entry/${entryId}/temporary-images?expiryHours=24`);
      console.log('📸 Image API Response:', response.data);
      
      if (response.data && response.data.success && response.data.data && response.data.data.images) {
        const imageData = response.data.data.images;
        
        // Extract non-null image paths
        const imageUrls = [
          imageData.imagePath,
          imageData.imagePath2,
          imageData.imagePath3,
          imageData.imagePath4,
          imageData.imagePath5
        ].filter(url => url !== null && url !== undefined && url !== '');
        
        console.log('✅ Found images:', imageUrls.length, imageUrls);
        
        if (imageUrls.length > 0) {
          setImageModal({ show: true, images: imageUrls, currentIndex: 0, entryId });
        } else {
          setImageModal({ show: true, images: [], currentIndex: 0, entryId, noImages: true });
        }
      } else {
        console.warn('⚠️ No images in response:', response.data);
        setImageModal({ show: true, images: [], currentIndex: 0, entryId, noImages: true });
      }
    } catch (error) {
      console.error('❌ Error fetching images:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      alert(`Failed to load images: ${error.response?.status || error.message}`);
    }
  };

  // ✅ Navigate images in modal
  const nextImage = () => {
    setImageModal(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  };

  const prevImage = () => {
    setImageModal(prev => ({
      ...prev,
      currentIndex: prev.currentIndex === 0 ? prev.images.length - 1 : prev.currentIndex - 1
    }));
  };

  // ✅ Close modal
  const closeModal = () => {
    setImageModal({ show: false, images: [], currentIndex: 0, entryId: null, noImages: false });
  };

  // ✅ Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!imageModal.show) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case 'Escape':
          closeModal();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [imageModal.show]);

  // ✅ Touch swipe support
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && imageModal.images.length > 1) {
      nextImage();
    }
    if (isRightSwipe && imageModal.images.length > 1) {
      prevImage();
    }
  };

  const visibleBlocks =
    roleId === 3 && savedBlockName
      ? blocks.filter((b) => b.name === savedBlockName)
      : blocks;

  const wasteColumns = [
    { key: "biodegradableKg", label: "Bio degradable(Kg)", colorClass: "text-green-700 font-medium" },
    { key: "nonDegradableKg", label: "Non degradable(Kg)", colorClass: "text-red-700 font-medium" },
    { key: "recyclableDryKg", label: "Recyclable (Kg)", colorClass: "text-blue-700 font-medium" },
    { key: "householdHazardousKg", label: "Household hazardous(Kg)", colorClass: "text-orange-700 font-medium" },
    { key: "mixedKg", label: "Mixed (Kg)", colorClass: "text-purple-700 font-medium" }
  ];

  const activeWasteColumns = wasteTypeFilter.length === 0
    ? wasteColumns
    : wasteColumns.filter(col => wasteTypeFilter.includes(col.key));

 return (
  <div className="mx-4 mt-0 cursor-default select-none">
    {/* 🔍 Search and Filters */}
   <div className="flex items-center gap-3 px-3 py-1 rounded-lg bg-white flex-shrink-0">

      {/* 🔍 Search */}
      <div className="w-64 flex-shrink-0">
        <StyledInput
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          
        />
      </div>

      {/* 🏗️ Block Filter */}
      {roleId !== 4 && (
       <FormControl sx={{ minWidth: 152 }} size="small">
                     <InputLabel shrink={true}>Blocks</InputLabel>
                     <Select
                       multiple
                       displayEmpty
                       value={Array.isArray(blockFilter) ? blockFilter : []}
                       onChange={(e) => {
                         const value = e.target.value;
                         if (value.includes("selectAll")) return;
                         setBlockFilter(value);
                       }}
                       input={<OutlinedInput label="Blocks" />}
                       renderValue={(selected) => {
                         if (roleId === 3 && savedBlockName) return savedBlockName;
                         if (selected.length === 0) return "Select Blocks";
                         return `${selected.length} Selected`;
                       }}
                       MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 180 } } }}
                       disabled={roleId === 3}
                     >
                       <MenuItem
                         value="selectAll"
                         onClick={() => {
                           if (blockFilter.length === visibleBlocks.length) {
                             setBlockFilter([]);
                           } else {
                             setBlockFilter(visibleBlocks.map((b) => b.name));
                           }
                         }}
                       >
                         <Checkbox
                           checked={
                             blockFilter.length > 0 &&
                             blockFilter.length === visibleBlocks.length
                           }
                           indeterminate={
                             blockFilter.length > 0 &&
                             blockFilter.length < visibleBlocks.length
                           }
                           sx={{ p: 0.3, transform: "scale(0.9)" }}
                         />
                         <ListItemText
                           primary="Select All"
                           primaryTypographyProps={{
                             fontSize: "0.8rem",
                             fontWeight: 600,
                           }}
                         />
                       </MenuItem>
       
                       {visibleBlocks.map((block) => (
                         <MenuItem
                           key={block.blockId}
                           value={block.name}
                           sx={{ py: 0.3, minHeight: "28px" }}
                         >
                           <Checkbox
                             checked={blockFilter.includes(block.name)}
                             sx={{ p: 0.3, transform: "scale(0.9)" }}
                           />
                           <ListItemText
                             primary={block.name}
                             primaryTypographyProps={{ fontSize: "0.8rem" }}
                           />
                         </MenuItem>
                       ))}
                     </Select>
                   </FormControl>
      )}

      {/* 🏘️ Panchayat Filter */}
                <FormControl sx={{ minWidth: 182 }} size="small">
                  <InputLabel shrink={true}>Panchayats</InputLabel>
                  <Select
                    multiple
                    displayEmpty
                    value={
                      roleId === 4 && savedPanchayatName
                        ? [savedPanchayatName]
                        : Array.isArray(panchayatFilter)
                        ? panchayatFilter
                        : []
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.includes("selectAll")) return;
                      setPanchayatFilter(value);
                    }}
                    input={<OutlinedInput label="Panchayats" />}
                    renderValue={(selected) => {
                      if (roleId === 4 && savedPanchayatName) return savedPanchayatName;
                      if (selected.length === 0) return "Select Panchayats";
                      return `${selected.length} Selected`;
                    }}
                    disabled={roleId === 4}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 180 } } }}
                  >
                    {panchayats.length > 0 && (
                      <MenuItem
                        value="selectAll"
                        onClick={() => {
                          if (panchayatFilter.length === panchayats.length) {
                            setPanchayatFilter([]);
                          } else {
                            setPanchayatFilter(panchayats.map((p) => p.name));
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            panchayatFilter.length > 0 &&
                            panchayatFilter.length === panchayats.length
                          }
                          indeterminate={
                            panchayatFilter.length > 0 &&
                            panchayatFilter.length < panchayats.length
                          }
                          sx={{ p: 0.3, transform: "scale(0.9)" }}
                        />
                        <ListItemText
                          primary="Select All"
                          primaryTypographyProps={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        />
                      </MenuItem>
                    )}
      
                    {panchayats.length > 0 ? (
                      panchayats.map((p, idx) => (
                        <MenuItem key={idx} value={p.name} sx={{ py: 0.3, minHeight: "28px" }}>
                          <Checkbox
                            checked={panchayatFilter.includes(p.name)}
                            sx={{ p: 0.3, transform: "scale(0.9)" }}
                          />
                          <ListItemText
                            primary={p.name}
                            primaryTypographyProps={{ fontSize: "0.8rem" }}
                          />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled sx={{ fontSize: "0.8rem", color: "#888" }}>
                        No Panchayats Available
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
      
      {/* 🗑️ Waste Type Filter */}
      <FormControl sx={{ minWidth: 170 }} size="small">
        <InputLabel shrink={true}>Waste Type</InputLabel>
        <Select
          multiple
          displayEmpty
          value={Array.isArray(wasteTypeFilter) ? wasteTypeFilter : []}
          onChange={(e) => {
            const value = e.target.value;
            if (value.includes("selectAll")) return;
            setWasteTypeFilter(value);
            setCurrentPage(1);
          }}
          input={<OutlinedInput label="Waste Type" />}
          renderValue={(selected) => {
            if (selected.length === 0) return "Select Waste Types";
            return `${selected.length} Selected`;
          }}
          MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 170 } } }}
        >
          <MenuItem
            value="selectAll"
            onClick={() => {
              if (wasteTypeFilter.length === 5) {
                setWasteTypeFilter([]);
              } else {
                setWasteTypeFilter([
                  "biodegradableKg",
                  "nonDegradableKg",
                  "recyclableDryKg",
                  "householdHazardousKg",
                  "mixedKg",
                ]);
              }
            }}
          >
            <Checkbox
              checked={wasteTypeFilter.length === 5}
              indeterminate={wasteTypeFilter.length > 0 && wasteTypeFilter.length < 5}
              sx={{ p: 0.3, transform: "scale(0.9)" }}
            />
            <ListItemText
              primary="Select All"
              primaryTypographyProps={{
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            />
          </MenuItem>

          {[
            { key: "biodegradableKg", label: "Bio degradable" },
            { key: "nonDegradableKg", label: "Non degradable" },
            { key: "recyclableDryKg", label: "Recyclable" },
            { key: "householdHazardousKg", label: "Household hazardous" },
            { key: "mixedKg", label: "Mixed" },
          ].map((item) => (
            <MenuItem key={item.key} value={item.key} sx={{ py: 0.3, minHeight: "28px" }}>
              <Checkbox
                checked={wasteTypeFilter.includes(item.key)}
                sx={{ p: 0.3, transform: "scale(0.9)" }}
              />
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: "0.8rem" }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 📅 Right Section */}
      <div className="flex items-center gap-2 ml-auto flex-shrink-0 whitespace-nowrap">

        {/* Quick Buttons */}
        <div className="flex gap-1">
          {["Today", "Week", "Month"].map((r) => (
            <ModernButton
              key={r}
              onClick={() => handleDateFilter(r)}
              isActive={activeRange === r}
            >
              {r}
            </ModernButton>
          ))}
        </div>

        {/* Date Pickers */}
        <div className="flex items-center gap-1 px-2 py-1 border border-purple-600 rounded-md bg-white">
          <DatePicker
            selected={fromDate ? new Date(fromDate) : null}
            onChange={(d) =>
              setFromDate(
                d
                  ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
                  : ""
              )
            }
            customInput={<IconDateButton label="From" />}
            maxDate={toDate ? new Date(toDate) : new Date()}
          />
          <DatePicker
            selected={toDate ? new Date(toDate) : null}
            onChange={(d) =>
              setToDate(
                d
                  ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
                  : ""
              )
            }
            customInput={<IconDateButton label="To" />}
            minDate={fromDate ? new Date(fromDate) : null}
            maxDate={new Date()}
          />
        </div>

        {/* Export */}
        <AnimatedExportButton
          onExportExcel={exportExcel}
          onExportPDF={exportPDF}
        />
      </div>
    </div>

      {/* 🧾 Data Table */}
      <div className="mt-4 bg-white rounded-lg shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead className="bg-gradient-to-r from-green-700 to-green-500 shadow-md">
              <tr>
                {[
                  "S.No",
                  "Date",
                  "Block",
                  "Panchayat",
                  "Vehicle #",
                  "Type",
                  ...activeWasteColumns.map(col => col.label),
                  "Total (Kg)",
                  "Images",
                ].map((h, i) => (
                  <th
                    key={i}
                    className="text-left px-4 py-3 text-xs text-white font-semibold tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {currentRecords.map((d, idx) => (
                <tr
                  key={d.entryId}
                  className={`border-b transition-all duration-200 ${
                    idx % 2 === 0 ? "bg-[#f8fafc]" : "bg-[#edf7f1]"
                  } hover:bg-[#d7f3e3] hover:shadow-md`}
                >
                  <td className="px-4 py-3 text-xs text-gray-700 font-medium">
                    {indexOfFirstRecord + idx + 1}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-700 whitespace-nowrap">{formatDate(d.date)}</td>
                  <td className="px-2 py-2 text-xs text-gray-700">{d.blockName}</td>
                  <td className="px-2 py-2 text-xs text-gray-700">{d.panchayatName}</td>
                  <td className="px-2 py-2 text-xs text-gray-700">{d.vehicleNumber}</td>
                  <td className="px-2 py-2 text-xs text-gray-700">{d.vehicleType}</td>
                  {activeWasteColumns.map((col) => (
                    <td key={col.key} className={`px-2 py-2 text-xs ${col.colorClass}`}>
                      {d[col.key]}
                    </td>
                  ))}
                  <td className="px-2 py-2 text-xs font-semibold text-gray-900">{d.totalKg}</td>
                  <td className="px-2 py-2">
                    <button
                      onClick={() => handleImageClick(d.entryId)}
                      className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                      title="View Images"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-2 flex items-center justify-between">
          <div className="text-xs text-neutral-500">
            Showing {currentRecords.length} of {filteredData.length} records
          </div>
          {/* <div className="flex items-center space-x-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={`px-3 py-1 rounded-md border ${
                  currentPage === i + 1
                    ? "bg-green-600 text-white"
                    : "bg-white text-neutral-700"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div> */}

          <div className="flex items-center space-x-1">

  {/* Prev */}
  <button
    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
    className="px-3 py-1 rounded-md border bg-white text-neutral-700 disabled:opacity-50"
    disabled={currentPage === 1}
  >
    Prev
  </button>

  {/* Page Numbers */}
  {getVisiblePages().map((page) => (
    <button
      key={page}
      onClick={() => handlePageChange(page)}
      className={`px-2 py-0.5 text-xs rounded-md border ${
        currentPage === page
          ? "bg-green-600 text-white"
          : "bg-white text-neutral-700"
      }`}
    >
      {page}
    </button>
  ))}

  {/* Next */}
  <button
    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
    className="px-3 py-1 rounded-md border bg-white text-neutral-700 disabled:opacity-50"
    disabled={currentPage === totalPages}
  >
    Next
  </button>

</div>

        </div>
      </div>

      {/* 🖼️ Image Modal */}
      {imageModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={closeModal}>
          {imageModal.noImages ? (
            /* No Images Available Modal */
            <div className="bg-white rounded-lg p-8 text-center max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <svg className="w-20 h-20 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Sorry!</h3>
              <p className="text-gray-600 mb-6">Photos are not available for this waste entry</p>
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            /* Regular Image Modal */
            <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full mx-4 flex overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Left side - Images */}
            <div 
              className="flex-1 relative bg-gray-100"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {imageModal.images.length > 0 && (
                <>
                  <img
                    src={imageModal.images[imageModal.currentIndex]}
                    alt={`Waste entry ${imageModal.entryId} - Image ${imageModal.currentIndex + 1}`}
                    className="w-full h-full object-contain"
                    onLoad={() => console.log('✅ Image loaded successfully')}
                    onError={(e) => {
                      console.error('❌ Image failed to load:', e.target.src);
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4=';
                    }}
                  />
                  
                  {/* Navigation arrows */}
                  {imageModal.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  )}
                  
                  {/* Image counter */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                    {imageModal.currentIndex + 1} / {imageModal.images.length}
                  </div>
                </>
              )}
            </div>
            
            {/* Right side - Waste entry details */}
            <div className="w-80 bg-white p-6 overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Waste Entry Details</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {(() => {
                const entry = wasteData.find(d => d.entryId === imageModal.entryId);
                if (!entry) return <p>Entry not found</p>;
                
                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-600">Date:</span>
                        <p className="text-gray-800">{formatDate(entry.date)}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Block:</span>
                        <p className="text-gray-800">{entry.blockName}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Panchayat:</span>
                        <p className="text-gray-800">{entry.panchayatName}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Vehicle:</span>
                        <p className="text-gray-800">{entry.vehicleNumber}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Type:</span>
                        <p className="text-gray-800">{entry.vehicleType}</p>
                      </div>
                    </div>
                    
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-600 mb-3">Waste Collection (Kg)</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-green-600">Bio Degradable:</span>
                          <span className="font-medium">{entry.biodegradableKg}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-red-600">Non Degradable:</span>
                          <span className="font-medium">{entry.nonDegradableKg}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-600">Recyclable:</span>
                          <span className="font-medium">{entry.recyclableDryKg}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-orange-600">Household Hazardous:</span>
                          <span className="font-medium">{entry.householdHazardousKg}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-purple-600">Mixed:</span>
                          <span className="font-medium">{entry.mixedKg}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2 font-semibold">
                          <span>Total:</span>
                          <span>{entry.totalKg}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Image thumbnails for navigation */}
                    {imageModal.images.length > 1 && (
                      <div className="border-t pt-4">
                        <h4 className="font-medium text-gray-600 mb-3">Images ({imageModal.images.length})</h4>
                        <div className="flex gap-2 overflow-x-auto">
                          {imageModal.images.map((img, idx) => (
                            <button
                              key={idx}
                              onClick={() => setImageModal(prev => ({ ...prev, currentIndex: idx }))}
                              className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden ${
                                idx === imageModal.currentIndex ? 'border-blue-500' : 'border-gray-300'
                              }`}
                            >
                              <img
                                src={img}
                                alt={`Thumbnail ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          )}
        </div>
      )}


    </div>
  );
}
